import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

import { ENV } from "@/config/env";

const SITE_SCOPING_ENABLED = ENV.SITE_SCOPING_ENABLED;

export interface SiteInfo {
  siteId: string;
  siteName: string;
  identityName: string;
  isHeadquarters: boolean;
  description?: string;
}

interface SiteContextType {
  /** Whether site scoping feature is enabled */
  isEnabled: boolean;
  /** Currently selected site ID for filtering (null = all sites / no filter) */
  selectedSiteId: string | null;
  /** Set the selected site ID */
  setSelectedSiteId: (siteId: string | null) => void;
  /** Current user's assigned site (from session) */
  userSite: SiteInfo | null;
  /** Set the user's site info */
  setUserSite: (site: SiteInfo | null) => void;
  /** All available sites */
  availableSites: SiteInfo[];
  /** Set available sites */
  setAvailableSites: (sites: SiteInfo[]) => void;
  /** Whether the current user can select different sites (admin/BOD only) */
  canSelectSite: boolean;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

const SITE_FILTER_KEY = "site_filter";

export const SiteProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Load saved site filter from sessionStorage
  const [selectedSiteId, setSelectedSiteIdState] = useState<string | null>(
    () => {
      if (!SITE_SCOPING_ENABLED) return null;
      const saved = sessionStorage.getItem(SITE_FILTER_KEY);
      return saved || null;
    },
  );
  const [userSite, setUserSite] = useState<SiteInfo | null>(null);
  const [availableSites, setAvailableSites] = useState<SiteInfo[]>([]);

  // User can select site if they have no assigned site (admin/BOD) or are at headquarters
  const canSelectSite = !userSite?.siteId || userSite?.isHeadquarters === true;

  const setSelectedSiteId = useCallback((siteId: string | null) => {
    setSelectedSiteIdState(siteId);
    if (siteId) {
      sessionStorage.setItem(SITE_FILTER_KEY, siteId);
    } else {
      sessionStorage.removeItem(SITE_FILTER_KEY);
    }
  }, []);

  // Clear site filter when user logs out
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "access_token" && !e.newValue) {
        setSelectedSiteId(null);
        setUserSite(null);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [setSelectedSiteId]);

  return (
    <SiteContext.Provider
      value={{
        isEnabled: SITE_SCOPING_ENABLED,
        selectedSiteId,
        setSelectedSiteId,
        userSite,
        setUserSite,
        availableSites,
        setAvailableSites,
        canSelectSite,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error("useSiteContext must be used within SiteProvider");
  }
  return context;
};
